import React, { useState, useRef, useEffect } from "react";
import { observer } from "mobx-react-lite";
import { useTranslation } from "react-i18next";
import { stores } from "../context";
import { Language } from "../store/UIStore";
import { SUPPORTED_LANGUAGES, SupportedLanguage } from "../config/languages";

const LanguageSwitcher = observer(() => {
  const { ui } = stores;
  const { i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const languageDisplayMap: Record<SupportedLanguage, string> = {
    pl: "PL",
    en: "En",
    uk: "Ukr",
    de: "DE",
    ko: "KO",
  };

  const supportedLngs = SUPPORTED_LANGUAGES;

  const changeLanguage = (lng: string) => {
    ui.setLanguage(lng as Language);
    setIsOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const isSupportedLanguage = (lng: string): lng is SupportedLanguage =>
    (SUPPORTED_LANGUAGES as readonly string[]).includes(lng);

  const currentLang = isSupportedLanguage(i18n.language) ? i18n.language : "en";

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Switcher button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center justify-center px-3 py-2 rounded-md text-sm font-semibold text-stone-800 dark:text-stone-200 bg-stone-100 dark:bg-stone-800 hover:bg-stone-200 dark:hover:bg-stone-700 transition-colors border border-stone-200 dark:border-stone-700"
        aria-label="Change language"
      >
        <span>{languageDisplayMap[currentLang]}</span>
        <svg
          className={`w-4 h-4 ml-1 transition-transform ${
            isOpen ? "rotate-180" : ""
          }`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M19 9l-7 7-7-7"
          ></path>
        </svg>
      </button>

      {/* Dropdown menu */}
      {isOpen && (
        <div className="absolute left-0 mt-2 w-32 bg-white dark:bg-stone-800 border border-stone-200 dark:border-stone-700 rounded-md shadow-lg py-1 z-30">
          {supportedLngs.map((lng) => {
            const isSelected = i18n.language === lng;
            return (
              <button
                key={lng}
                onClick={() => changeLanguage(lng)}
                className={`block w-full text-left px-4 py-2 text-sm transition-colors ${
                  isSelected
                    ? "bg-emerald-50 text-emerald-800 font-semibold dark:bg-emerald-950/80 dark:text-emerald-300"
                    : "text-stone-700 dark:text-stone-200 hover:bg-stone-100 dark:hover:bg-stone-700"
                }`}
              >
                {languageDisplayMap[lng] || lng.toUpperCase()}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
});

export default LanguageSwitcher;
